export type DualRangeValue = [number, number];

export function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export function snapToStep(value: number, min: number, step: number) {
  if (step <= 0) return value;
  const snapped = min + Math.round((value - min) / step) * step;
  const decimals = String(step).split('.')[1]?.length ?? 0;
  return Number(snapped.toFixed(decimals));
}

export function valueFromTrackPointer({
  clientX,
  trackLeft,
  trackWidth,
  min,
  max,
}: {
  clientX: number;
  trackLeft: number;
  trackWidth: number;
  min: number;
  max: number;
}) {
  if (trackWidth <= 0) return min;
  const ratio = clamp((clientX - trackLeft) / trackWidth, 0, 1);
  return min + ratio * (max - min);
}

export function getClosestThumbIndex(
  values: DualRangeValue,
  next: number
): 0 | 1 {
  const distanceToMin = Math.abs(values[0] - next);
  const distanceToMax = Math.abs(values[1] - next);
  if (distanceToMax < distanceToMin) return 1;
  if (distanceToMin < distanceToMax) return 0;
  const midpoint = (values[0] + values[1]) / 2;
  return next < midpoint ? 0 : 1;
}

export function applyThumbMove({
  current,
  thumb,
  nextValue,
  min,
  max,
  step,
  minGap,
}: {
  current: DualRangeValue;
  thumb: 0 | 1;
  nextValue: number;
  min: number;
  max: number;
  step: number;
  minGap: number;
}): DualRangeValue {
  const snapped = clamp(snapToStep(nextValue, min, step), min, max);
  const gap = Math.max(step, minGap);

  if (thumb === 0) {
    return [Math.min(snapped, current[1] - gap), current[1]];
  }

  return [current[0], Math.max(snapped, current[0] + gap)];
}

export function parseCurrencyInput(raw: string) {
  const digits = raw.replace(/[^\d]/g, '');
  if (digits === '') return null;
  const amount = Number(digits);
  return Number.isFinite(amount) ? amount : null;
}

export function boundTicketPriceRange(
  current: DualRangeValue,
  bounds: { min: number; max: number },
  uninitialized: DualRangeValue = [0, 1000]
): DualRangeValue {
  const isUninitialized =
    current[0] === uninitialized[0] && current[1] === uninitialized[1];

  if (isUninitialized) {
    return [bounds.min, bounds.max];
  }

  const min = clamp(current[0], bounds.min, bounds.max);
  const max = clamp(current[1], bounds.min, bounds.max);

  if (min >= max) {
    return [bounds.min, bounds.max];
  }

  return [min, max];
}
